import { useState, useEffect } from "react";
import Tabs from "../../components/Tabs";
import MilestoneCard from "../../components/MilestoneCard";
import { useHeaderContext } from "../../context/headerContext";
import { useUserContext } from "../../context/userContext";
import SelectInput from "../../components/SelectInput";
import { differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays, parseISO } from "date-fns";
import { useLocation, useNavigate } from 'react-router-dom';
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function Milestones() {
  const { setTitle, setBackButton } = useHeaderContext();
  const [currentTab, setCurrentTab] = useState("milestones");
  const { user, participants, logout, updateTokens } = useUserContext();
  const [ milestones, setMilestones ] = useState([]);
  const [lastUpdatedText, setLastUpdatedText] = useState('');
  const location = useLocation();
  const reversedParticipants = participants?.slice().reverse();
  const [ selectedParticipant, setSelectedParticipant ] = useState(
    location.state?.selectedParticipant || 
    (reversedParticipants?.length > 0 ? reversedParticipants[0].ParticipantId : '')
  );
  const navigate = useNavigate();

  useEffect(() => {
    setTitle("Activity");
    setBackButton(true);
  }, []);
  
  useEffect(() => {
    getMilestones();
  }, [selectedParticipant]);

  const getMilestones = async () => {
    await Sentry.startSpan({ name: "Fetch Milestones", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Milestone/FetchMilestonesForParticipant?participantId=${selectedParticipant}`;
        span.setAttribute("http.url", url);
  
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          const data = await response.json();
          setMilestones(data);
        } else {
          Sentry.captureMessage("Failed to fetch milestones", { extra: { status: response.status, url } });
          throw new Error("Failed to fetch milestones");
        }
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };  

  useEffect(() => {
    const updateLastUpdatedText = () => {
      if (milestones.length === 0) return;

      const mostRecentMilestone = milestones.reduce((latest, milestone) => {
        const milestoneDate = milestone?.LastUpdated ? parseISO(milestone.LastUpdated) : new Date(0);
        return milestoneDate > latest ? milestoneDate : latest;
      }, new Date(0));

      const now = new Date();
      const diffInSeconds = differenceInSeconds(now, mostRecentMilestone);
      let timeText = '';

      if (diffInSeconds < 60) {
        timeText = `${diffInSeconds} SECS AGO`;
      } else if (diffInSeconds < 3600) {
        const diffInMinutes = differenceInMinutes(now, mostRecentMilestone);
        timeText = `${diffInMinutes} MINS AGO`;
      } else if (diffInSeconds < 86400) {
        const diffInHours = differenceInHours(now, mostRecentMilestone);
        timeText = `${diffInHours} HOURS AGO`;
      } else {
        const diffInDays = differenceInDays(now, mostRecentMilestone);
        timeText = `${diffInDays} DAYS AGO`;
      }

      setLastUpdatedText(timeText);
    };

    updateLastUpdatedText();
  }, [milestones]);

  const memberSinceDate = milestones[0]?.MemberSince?.split(' ')[0] || '';

  // Update selectedParticipant when location.state changes
  useEffect(() => {
    if (location.state?.selectedParticipant) {
      setSelectedParticipant(location.state.selectedParticipant);
    }
  }, [location.state.selectedParticipant]);

  const handleParticipantChange = (e) => {
    const newParticipantId = e.target.value;
    setSelectedParticipant(newParticipantId);
    navigate('.', { 
      replace: true, 
      state: { selectedParticipant: newParticipantId } 
    });
  };

  return (
    <div className="flex flex-col h-full">
      <Tabs
        tabs={["log", "milestones"]}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        selectedParticipant={selectedParticipant}
      />

       <div className="bg-sportsLightGrey pt-7 pl-5 pr-7 flex flex-col h-full pb-[85px]">
        <div className="mb-8">
          <p className="font-poppins text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-1">
            LAST UPDATED • <span>{lastUpdatedText}</span>
          </p>
          <p className="font-poppins text-[32px] font-semibold leading-[42px]">
            Your milestones
          </p>
        </div>

        {participants?.length > 0 && (
          <div className="flex flex-col gap-5">
            <p className="font-medium">Participant</p>
            <SelectInput 
              onChange={handleParticipantChange} 
              name="participantPicker" 
              value={selectedParticipant}
              options={participants?.slice().reverse().map((participant, index) => (
                <option value={participant.ParticipantId} key={index}>
                  {participant.ParticipantName}
                </option>
              ))} 
            />

            <p className="text-sportsDarkGrey">Member Since: {memberSinceDate}</p>
            
            <div className="flex flex-col gap-2">
              <p className="font-poppins text-[12px] leading-6 tracking-[0.2em] text-sportsBlack font-semibold">
                COMPLETE ({milestones?.filter((milestone) => milestone?.AwardedCount >= milestone?.AwardedAfterEvents)?.length})
              </p>
              {milestones?.filter((milestone) => milestone?.AwardedCount >=  milestone?.AwardedAfterEvents)?.map((milestone) => (
                  <MilestoneCard 
                    milestone={milestone} 
                    key={milestone?.MilestoneName}
                    selectedParticipant={selectedParticipant}
                  />
                ))}
            </div>

            <div className="flex flex-col gap-2">
              <p className="font-poppins text-[12px] leading-6 tracking-[0.2em] text-sportsBlack font-semibold">
                INCOMPLETE ({milestones?.filter((milestone) => milestone?.AwardedCount < milestone?.AwardedAfterEvents)?.length})
              </p>
              {milestones?.filter((milestone) => milestone?.AwardedCount < milestone?.AwardedAfterEvents)?.map((milestone) => (
                  <MilestoneCard 
                    milestone={milestone} 
                    key={milestone?.MilestoneName}
                    selectedParticipant={selectedParticipant}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
